import { useEffect, useRef } from 'react';
import useAnalyticsContext from '|/hooks/use-analytics-context';

function uuid(seed: string | null = null): string {
  const seedInt = seed === null ? null : parseInt(seed);
  return seedInt !== null
    ? (seedInt ^ ((Math.random() * 16) >> (seedInt / 4))).toString(16)
    : `10000000${-1e3}${-4e3}${-8e3}${-1e11}`.replace(/[018]/g, uuid);
}

const useSessionTracking = () => {
  const { trackEvent } = useAnalyticsContext();
  const sessionGuid = useRef<string | null>(null);
  const sessionStatus = useRef<'inProcess' | 'ended' | null>(null);

  useEffect(() => {
    if (sessionStatus.current !== null) {
      trackEvent('pp_session', {
        sessionGuid: sessionGuid.current,
        action: 'noop',
        source: 'applicationLoad'
      });
    } else {
      sessionStatus.current = 'inProcess';
      const newSessionGuid = uuid();
      sessionGuid.current = newSessionGuid;
      trackEvent('pp_session', {
        sessionGuid: newSessionGuid,
        action: 'started',
        source: 'applicationLoad'
      });
      const sendEventOnVisibilityChange = () => {
        if (
          document.visibilityState === 'hidden' &&
          sessionStatus.current !== 'ended'
        ) {
          sessionStatus.current = 'ended';
          trackEvent('pp_session', {
            sessionGuid: sessionGuid.current,
            action: 'ended',
            source: 'visibilityChange'
          });
        } else if (
          document.visibilityState === 'visible' &&
          sessionStatus.current !== 'inProcess'
        ) {
          sessionStatus.current = 'inProcess';
          trackEvent('pp_session', {
            sessionGuid: sessionGuid.current,
            action: 'resumed',
            source: 'visibilityChange'
          });
        } else {
          trackEvent('pp_session', {
            sessionGuid: sessionGuid.current,
            action: 'noop',
            source: 'visibilityChange'
          });
        }
      };
      const sendEventBeforeUnload = () => {
        if (
          document.visibilityState === 'visible' &&
          sessionStatus.current !== 'ended'
        ) {
          sessionStatus.current = 'ended';
          trackEvent('pp_session', {
            sessionGuid: sessionGuid.current,
            action: 'ended',
            source: 'beforeUnload'
          });
        } else {
          trackEvent('pp_session', {
            sessionGuid: sessionGuid.current,
            action: 'noop',
            source: 'beforeUnload'
          });
        }
      };
      document.addEventListener(
        'visibilitychange',
        sendEventOnVisibilityChange
      );
      window.addEventListener('beforeunload', sendEventBeforeUnload);
      return () => {
        document.removeEventListener(
          'visibilitychange',
          sendEventOnVisibilityChange
        );
        window.removeEventListener('beforeunload', sendEventBeforeUnload);
      };
    }
  }, [trackEvent]);
};

export default useSessionTracking;
